/**
 * @lodex-pro/web-shared/auth/sign-in-screen-view — the ONE sign-in screen for every web app.
 *
 * Corbin, 2026-10-21: "All sign in screens should be the same." Before this, web-admin,
 * web-dispatch, web-billing and web-portal each assembled their own page from the same three
 * pieces, and they had drifted: different headings and subtitles, a filled blue "Forgot your
 * password?" button on some, none at all on the admin console, and a billing page with no card.
 *
 * This component owns the layout and the step discriminator; each app passes only its name and
 * what happens after sign-in:
 *
 *   sign-in       <EmailTotpFlow> (email + password, then MFA), with "Forgot your password?"
 *                 as a link right under its Sign in button
 *   force-change  <ForcedPasswordChange> on CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED (DEC-152)
 *   forgot        <ForgotPassword>; Back and a finished reset return to sign-in
 *
 * Every password field in those steps is a <PasswordField> (show/hide eye). The markup is
 * styled by design/auth.css, which every app imports after tokens.css.
 *
 * The screen is built from its three flow pieces by makeSignInScreen, so an app test that
 * stubs those pieces (each app mocks @lodex-pro/web-shared whole) still renders THIS layout and
 * step logic rather than a hand-written copy. sign-in-screen.tsx builds the real one.
 */

import { useState } from 'react';
import type { ComponentType, FC } from 'react';

import type { EmailTotpFlowProps } from './email-totp-flow.js';
import type { ForcedPasswordChangeProps } from './forced-password-change.js';
import type { ForgotPasswordProps } from './forgot-password.js';

export interface SignInScreenProps {
  /** The app's name as the heading shows it, e.g. "lodeX Pro Operator Portal". */
  readonly appName: string;
  /** Called once the user is signed in (also after a forced password change). */
  readonly onSignedIn: () => void;
  /** Called when Cognito asks the user to set up an authenticator app. */
  readonly onTotpSetupRequired: () => void;
}

/** The flow pieces the screen is made of. */
export interface SignInScreenParts {
  readonly EmailTotpFlow: ComponentType<EmailTotpFlowProps>;
  readonly ForcedPasswordChange: ComponentType<ForcedPasswordChangeProps>;
  readonly ForgotPassword: ComponentType<ForgotPasswordProps>;
}

export function makeSignInScreen({
  EmailTotpFlow,
  ForcedPasswordChange,
  ForgotPassword,
}: SignInScreenParts): FC<SignInScreenProps> {
  const SignInScreen: FC<SignInScreenProps> = ({ appName, onSignedIn, onTotpSetupRequired }) => {
    const [stage, setStage] = useState<'sign-in' | 'force-change' | 'forgot'>('sign-in');
    return (
      <main className="auth-shell" aria-label={`Sign in to ${appName}`}>
        <div className="auth-card">
          {/* Only the app's name differs between apps, e.g. "lodeX Pro Operator Portal — Sign in". */}
          <h1 className="auth-brand">
            {appName} —{' '}
            {stage === 'forgot'
              ? 'Reset password'
              : stage === 'force-change'
                ? 'New password'
                : 'Sign in'}
          </h1>
          {stage === 'sign-in' && (
            <EmailTotpFlow
              onSignedIn={onSignedIn}
              onTotpSetupRequired={onTotpSetupRequired}
              onNewPasswordRequired={() => {
                setStage('force-change');
              }}
              onForgotPassword={() => {
                setStage('forgot');
              }}
            />
          )}
          {stage === 'force-change' && <ForcedPasswordChange onCompleted={onSignedIn} />}
          {stage === 'forgot' && (
            <ForgotPassword
              onCancel={() => {
                setStage('sign-in');
              }}
              onReset={() => {
                setStage('sign-in');
              }}
            />
          )}
        </div>
      </main>
    );
  };
  return SignInScreen;
}
